import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ImportService } from '../import.service';
import { DataService } from '../data.service';
import {
  TransformationService,
  ImportValidationResult,
  ImportValidationSummary,
  formatValidationForClipboard,
  copyToClipboard
} from '@luzula/luzula-lib';
import { AuthService } from '../auth.service';
import { AufnahmePunkt, AufnahmePunktDocument, punktartFromSymbol, punktTemplate, CURRENT_VERSION, LocationSystem, istPunktartMitProtokoll, ImportInfo } from '../data-model';
import { LatLng } from 'leaflet';
import * as XLSX from 'xlsx';

type ImportStep = 'select' | 'select-layer' | 'analyzing' | 'preview' | 'importing' | 'complete';

interface GpkgLayerChoice {
  file: File;
  layers: string[];
  selectedLayer: string;
}

interface ParsedFile {
  filename: string;
  sheets: ParsedSheet[];
}

interface ParsedSheet {
  name: string;
  format: 'bohrpunkt' | 'moorpunkt' | 'unknown';
  validation: ImportValidationResult;
  data?: AufnahmePunkt;
  importInfo?: ImportInfo;  // Wird beim Import gesetzt
}

interface DuplicateInfo {
  newPoint: AufnahmePunkt;
  existingId: string;
  existingNummer?: string;
  matchType: 'koordinaten' | 'nummer' | 'datum_kartierer';
  distance?: number;
}

@Component({
  selector: 'bp-import-wizard',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbProgressbarModule],
  templateUrl: './import-wizard.component.html',
  styleUrls: ['./import-wizard.component.css']
})
export class ImportWizardComponent implements OnInit {
  step: ImportStep = 'select';

  // Dateiauswahl
  excelFiles: File[] = [];
  gpkgFiles: File[] = [];

  // Analyse-Ergebnisse
  parsedFiles: ParsedFile[] = [];
  validationSummary: ImportValidationSummary | null = null;

  // Duplikate
  duplicates: DuplicateInfo[] = [];

  // Import-Optionen
  importOptions = {
    importValid: true,
    importWithWarnings: true,
    duplicateAction: 'skip' as 'skip' | 'update' | 'keep_both'
  };

  // Filter für Anzeige: 'all' | 'success' | 'warning' | 'error'
  displayFilter: 'all' | 'success' | 'warning' | 'error' = 'all';

  toggleFilter(filter: 'success' | 'warning' | 'error'): void {
    this.displayFilter = this.displayFilter === filter ? 'all' : filter;
  }

  sheetMatchesFilter(sheet: ParsedSheet): boolean {
    if (this.displayFilter === 'all') return true;
    if (this.displayFilter === 'error') return !sheet.validation.isValid;
    if (this.displayFilter === 'warning') return sheet.validation.isValid && sheet.validation.hasWarnings;
    if (this.displayFilter === 'success') return sheet.validation.isValid && !sheet.validation.hasWarnings;
    return true;
  }

  // Fortschritt
  progress = 0;
  progressText = '';

  // Ergebnis
  importedCount = 0;
  skippedCount = 0;

  // Cache für bestehende Punkte (für Duplikatprüfung)
  private existingPoints: AufnahmePunktDocument[] = [];

  // Layer-Auswahl für GeoPackage mit mehreren Punkt-Layern
  gpkgLayerChoices: GpkgLayerChoice[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private importService: ImportService,
    private dataService: DataService,
    private transformService: TransformationService,
    private toastr: ToastrService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {}

  // === Dateiauswahl ===

  onExcelFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.excelFiles = Array.from(input.files);
    }
  }

  onGpkgFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.gpkgFiles = Array.from(input.files);
    }
  }

  removeExcelFile(index: number): void {
    this.excelFiles.splice(index, 1);
  }

  removeGpkgFile(index: number): void {
    this.gpkgFiles.splice(index, 1);
  }

  get hasFiles(): boolean {
    return this.excelFiles.length > 0 || this.gpkgFiles.length > 0;
  }

  // === Analyse ===

  async startAnalysis(): Promise<void> {
    // Zuerst prüfen, ob Layer-Auswahl für GeoPackage nötig ist
    this.gpkgLayerChoices = [];

    for (const file of this.gpkgFiles) {
      if (file.name.toLowerCase().endsWith('.gpkg')) {
        const pointLayers = await this.getGpkgPointLayers(file);
        if (pointLayers.length > 1) {
          // Mehrere Punkt-Layer: Benutzer muss wählen
          this.gpkgLayerChoices.push({
            file,
            layers: pointLayers,
            selectedLayer: pointLayers[0]
          });
        }
      }
    }

    // Falls Layer-Auswahl nötig, erst zur Auswahl gehen
    if (this.gpkgLayerChoices.length > 0) {
      this.step = 'select-layer';
      return;
    }

    // Sonst direkt analysieren
    await this.runAnalysis();
  }

  async continueAfterLayerSelection(): Promise<void> {
    await this.runAnalysis();
  }

  private async runAnalysis(): Promise<void> {
    this.step = 'analyzing';
    this.progress = 0;
    this.parsedFiles = [];

    const totalFiles = this.excelFiles.length + this.gpkgFiles.length;
    let processedFiles = 0;

    // Excel-Dateien verarbeiten
    for (const file of this.excelFiles) {
      this.progressText = `Analysiere ${file.name}...`;
      try {
        const parsedFile = await this.parseExcelFile(file);
        this.parsedFiles.push(parsedFile);
      } catch (error: any) {
        this.parsedFiles.push({
          filename: file.name,
          sheets: [{
            name: file.name,
            format: 'unknown',
            validation: {
              source: file.name,
              isValid: false,
              hasWarnings: false,
              messages: [{
                severity: 'error',
                field: 'Datei',
                value: '',
                message: `Fehler beim Lesen: ${error.message}`
              }]
            }
          }]
        });
      }
      processedFiles++;
      this.progress = Math.round((processedFiles / totalFiles) * 100);
    }

    // GPKG/GeoJSON-Dateien verarbeiten
    for (const file of this.gpkgFiles) {
      this.progressText = `Analysiere ${file.name}...`;
      try {
        // Layer-Auswahl berücksichtigen
        const layerChoice = this.gpkgLayerChoices.find(c => c.file === file);
        const selectedLayer = layerChoice?.selectedLayer;
        const parsedFile = await this.parseGeoFile(file, selectedLayer);
        this.parsedFiles.push(parsedFile);
      } catch (error: any) {
        this.parsedFiles.push({
          filename: file.name,
          sheets: [{
            name: file.name,
            format: 'unknown',
            validation: {
              source: file.name,
              isValid: false,
              hasWarnings: false,
              messages: [{
                severity: 'error',
                field: 'Datei',
                value: '',
                message: `Fehler beim Lesen: ${error.message}`
              }]
            }
          }]
        });
      }
      processedFiles++;
      this.progress = Math.round((processedFiles / totalFiles) * 100);
    }

    // Zusammenfassung erstellen
    this.createValidationSummary();

    // Duplikate prüfen
    await this.checkDuplicates();

    this.step = 'preview';
  }

  private async parseExcelFile(file: File): Promise<ParsedFile> {
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, { cellDates: true });

    const sheets: ParsedSheet[] = [];

    for (const sheetName of wb.SheetNames) {
      // Akzeptiere nur:
      // - Numerische Namen (1, 2, 3...) für Bohrpunktprotokolle
      // - Moorpunkt-Namen wie "M1-23", "M2-23" (M + Zahl + optional Suffix)
      // - BPF-Sheets wie "BPF (lh001)", "BPF (lhP001)" etc.
      const isNumeric = !isNaN(parseInt(sheetName)) && /^\d+$/.test(sheetName);
      const isMoorpunkt = /^M\d+/i.test(sheetName);
      const isBPF = /^BPF\s*\(/i.test(sheetName);
      if (!isNumeric && !isMoorpunkt && !isBPF) continue;

      const ws = wb.Sheets[sheetName];
      const sheetData = XLSX.utils.sheet_to_json(ws, { blankrows: true, header: 'A' }) as any[];

      const format = this.importService.detectSheetFormat(sheetData);
      const validation: ImportValidationResult = {
        source: `${file.name} / ${sheetName}`,
        isValid: true,
        hasWarnings: false,
        messages: []
      };

      let parsedData: AufnahmePunkt | undefined;

      try {
        if (format === 'bohrpunkt') {
          parsedData = this.importService.bohrpunktFromExcelSheet(sheetData);

          // Info über Koordinaten-Transformation anzeigen
          const parseInfo = this.importService.lastParseInfo;
          if (parseInfo.coordTransform) {
            validation.messages.push({
              severity: 'info',
              field: 'Koordinaten',
              value: '',
              message: `Transformation: ${parseInfo.coordTransform}`
            });
          }
          if (parseInfo.pointType) {
            validation.messages.push({
              severity: 'info',
              field: 'Punkttyp',
              value: '',
              message: parseInfo.pointType
            });
          }
        } else if (format === 'moorpunkt') {
          parsedData = this.importService.moorpunktFromExcelSheet(sheetData);
        } else {
          validation.isValid = false;
          validation.messages.push({
            severity: 'error',
            field: 'Format',
            value: '',
            message: 'Unbekanntes Protokollformat'
          });
        }

        if (parsedData) {
          // Basis-Validierung
          this.validateParsedData(parsedData, validation);
        }
      } catch (error: any) {
        validation.isValid = false;
        validation.messages.push({
          severity: 'error',
          field: 'Parsing',
          value: '',
          message: error.message
        });
      }

      validation.data = parsedData;

      // Import-Info für späteres Speichern vorbereiten
      const parseInfo = this.importService.lastParseInfo;
      const importInfo: ImportInfo = {
        time: Date.now(),
        user: this.authService.currentUser || undefined,
        source: `${file.name} / ${sheetName}`,
        coordTransform: parseInfo.coordTransform
      };

      sheets.push({
        name: sheetName,
        format,
        validation,
        data: parsedData,
        importInfo
      });
    }

    return { filename: file.name, sheets };
  }

  private async parseGeoFile(file: File, selectedLayer?: string): Promise<ParsedFile> {
    const fileName = file.name.toLowerCase();
    let features: any[] = [];

    if (fileName.endsWith('.gpkg')) {
      features = await this.parseGeoPackage(file, selectedLayer);
    } else if (fileName.endsWith('.geojson') || fileName.endsWith('.json')) {
      features = await this.parseGeoJSON(file);
    } else {
      throw new Error('Nicht unterstütztes Dateiformat');
    }

    // Features zu Punkten konvertieren
    const sheets: ParsedSheet[] = [];
    let pointIndex = 0;

    for (const feature of features) {
      pointIndex++;

      const validation: ImportValidationResult = {
        source: `${file.name} / Feature ${pointIndex}`,
        isValid: true,
        hasWarnings: false,
        messages: []
      };

      try {
        const parsedData = this.convertFeatureToPoint(feature);
        if (parsedData) {
          this.validateParsedData(parsedData, validation);
          validation.data = parsedData;

          sheets.push({
            name: `Feature ${pointIndex}`,
            format: 'bohrpunkt', // GeoJSON/GPKG werden als generische Punkte behandelt
            validation,
            data: parsedData
          });
        }
      } catch (error: any) {
        validation.isValid = false;
        validation.messages.push({
          severity: 'error',
          field: 'Konvertierung',
          value: '',
          message: error.message
        });
        sheets.push({
          name: `Feature ${pointIndex}`,
          format: 'unknown',
          validation
        });
      }
    }

    return { filename: file.name, sheets };
  }

  private async parseGeoJSON(file: File): Promise<any[]> {
    const text = await file.text();
    const geojson = JSON.parse(text);

    if (geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) {
      throw new Error('Ungültiges GeoJSON: FeatureCollection erwartet');
    }

    // Nur Punkt-Features zurückgeben
    return geojson.features.filter((f: any) =>
      f.geometry && f.geometry.type === 'Point'
    );
  }

  /**
   * Ermittelt alle Punkt-Layer in einem GeoPackage
   */
  private async getGpkgPointLayers(file: File): Promise<string[]> {
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    const { GeoPackageAPI } = await import('@ngageoint/geopackage');
    const geoPackage = await GeoPackageAPI.open(uint8Array);

    const featureTables = geoPackage.getFeatureTables();
    const pointLayers: string[] = [];

    console.log('GeoPackage Feature-Tabellen:', featureTables);

    for (const tableName of featureTables) {
      const featureDao = geoPackage.getFeatureDao(tableName);

      // Geometrie-Typ aus der FeatureDao-Definition prüfen
      const geomColType = (featureDao as any).geometryType || (featureDao as any).table?.geometryType;
      console.log(`Layer "${tableName}": geometryType = "${geomColType}"`);

      // Punkt-Layer erkennen (case-insensitive)
      const geomTypeStr = String(geomColType || '').toUpperCase();
      const isPointLayer = geomTypeStr === 'POINT' || geomTypeStr === 'MULTIPOINT' || geomTypeStr.includes('POINT');

      if (isPointLayer) {
        pointLayers.push(tableName);
        console.log(`  -> Punkt-Layer erkannt!`);
      } else {
        // Fallback: Erste Zeile prüfen via GeoJSON-Konvertierung
        let hasPoints = false;
        const rows = featureDao.queryForAll();
        if (rows.length > 0) {
          const firstRow = rows[0] as any;
          const geom = firstRow['geometry'] || firstRow.geometry;
          if (geom && typeof geom.toGeoJSON === 'function') {
            const geojson = geom.toGeoJSON();
            console.log(`  -> Erste Geometrie: type="${geojson?.type}"`);
            if (geojson?.type === 'Point' || geojson?.type === 'MultiPoint') {
              hasPoints = true;
              pointLayers.push(tableName);
              console.log(`  -> Punkt-Layer erkannt (via GeoJSON)!`);
            }
          } else {
            console.log(`  -> Keine Geometrie oder toGeoJSON nicht verfügbar`);
          }
        }
      }
    }

    return pointLayers;
  }

  private async parseGeoPackage(file: File, selectedLayer?: string): Promise<any[]> {
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    // Dynamic import für GeoPackage API
    const { GeoPackageAPI } = await import('@ngageoint/geopackage');
    const geoPackage = await GeoPackageAPI.open(uint8Array);

    const featureTables = geoPackage.getFeatureTables();
    if (featureTables.length === 0) {
      throw new Error('Keine Feature-Tabellen im GeoPackage gefunden');
    }

    // Layer bestimmen: explizit gewählt, oder automatisch
    let tableName: string;
    if (selectedLayer) {
      tableName = selectedLayer;
    } else {
      // Automatische Auswahl: Punkt-Layer finden
      const pointLayers = await this.getGpkgPointLayers(file);
      if (pointLayers.length === 0) {
        // Fallback: Alle Feature-Tabellen anzeigen
        console.warn('Keine Punkt-Layer gefunden, verfügbare Tabellen:', featureTables);
        throw new Error(`Keine Punkt-Layer im GeoPackage gefunden. Verfügbare Layer: ${featureTables.join(', ')}`);
      }
      // Bevorzuge Layer mit "punkt" im Namen, sonst den ersten
      tableName = pointLayers.find(t => t.toLowerCase().includes('punkt')) || pointLayers[0];
    }

    console.log(`Verwende Layer: "${tableName}"`);

    // SRS (Koordinatensystem) des Layers ermitteln
    const featureDao = geoPackage.getFeatureDao(tableName);
    const srs = featureDao.srs;
    const epsgCode = srs?.organization_coordsys_id;
    console.log('Layer SRS:', srs?.srs_name, 'EPSG:', epsgCode);

    // Verwende iterateGeoJSONFeatures für korrekte GeoJSON-Konvertierung
    const features: any[] = [];

    // Diese Methode liefert direkt GeoJSON-Features mit korrekter Geometrie
    const featureIterator = geoPackage.iterateGeoJSONFeatures(tableName);
    for (const feature of featureIterator) {
      if (feature.geometry &&
          (feature.geometry.type === 'Point' || feature.geometry.type === 'MultiPoint')) {
        // EPSG-Code hinzufügen für spätere Transformation
        (feature as any).crs = epsgCode;
        features.push(feature);
      }
    }

    console.log(`${features.length} Punkt-Features gefunden`);
    if (features.length > 0) {
      console.log('Beispiel-Feature:', features[0]);
      console.log('Beispiel-Koordinaten:', features[0].geometry.coordinates);
    }

    return features;
  }

  private convertFeatureToPoint(feature: any): AufnahmePunkt | null {
    if (!feature.geometry || feature.geometry.type !== 'Point') {
      return null;
    }

    const coords = feature.geometry.coordinates;
    if (!coords || coords.length < 2) {
      return null;
    }

    const props = feature.properties || {};
    const crs = feature.crs; // EPSG-Code aus GeoPackage

    // Debug: Alle Properties ausgeben
    console.log('GeoPackage Feature Properties:', Object.keys(props), props);

    // SYMBOL zu Punktart konvertieren
    const symbol = parseInt(props.SYMBOL || props.symbol || '6', 10);
    const punktart = punktartFromSymbol(symbol) || '6_Tiefbohrung';

    // Neuen Punkt aus Template erstellen
    const bp = punktTemplate(punktart);

    // Koordinaten transformieren basierend auf tatsächlichem Format
    // GeoJSON-Standard ist immer WGS84 (EPSG:4326), auch wenn das GeoPackage ein anderes CRS hat
    // Die iterateGeoJSONFeatures-Methode konvertiert bereits zu WGS84
    let epsg5650: { x: number; y: number };

    // Heuristik: Wenn X < 180 und Y < 90, dann sind es WGS84 Lon/Lat Koordinaten
    const isWGS84 = Math.abs(coords[0]) <= 180 && Math.abs(coords[1]) <= 90;

    if (isWGS84) {
      // WGS84 (GeoJSON-Standard) - coords sind [lon, lat]
      const latlng = new LatLng(coords[1], coords[0]);
      const transformed = this.transformService.toEPSG5650(latlng);
      epsg5650 = { x: transformed.x, y: transformed.y };
      console.log(`Koordinaten: WGS84 [${coords[0]}, ${coords[1]}] → EPSG:5650 [${epsg5650.x}, ${epsg5650.y}]`);
    } else if (coords[0] > 32000000 && coords[0] < 34000000) {
      // Bereits EPSG:5650 (mit Zone-Präfix 33)
      epsg5650 = { x: coords[0], y: coords[1] };
      console.log(`Koordinaten: Bereits EPSG:5650 [${epsg5650.x}, ${epsg5650.y}]`);
    } else if (coords[0] > 200000 && coords[0] < 800000 && coords[1] > 5000000) {
      // EPSG:25833 (UTM Zone 33N ohne Präfix)
      const transformed = this.transformService.toEPSG5650(
        this.transformService.from({ system: LocationSystem.EPSG25833, x: coords[0], y: coords[1] })
      );
      epsg5650 = { x: transformed.x, y: transformed.y };
      console.log(`Koordinaten: EPSG:25833 [${coords[0]}, ${coords[1]}] → EPSG:5650 [${epsg5650.x}, ${epsg5650.y}]`);
    } else {
      // Fallback: Als WGS84 behandeln
      console.warn(`Unbekanntes Koordinatenformat: [${coords[0]}, ${coords[1]}], versuche als WGS84`);
      const latlng = new LatLng(coords[1], coords[0]);
      const transformed = this.transformService.toEPSG5650(latlng);
      epsg5650 = { x: transformed.x, y: transformed.y };
    }

    bp.header.koordinaten = {
      system: LocationSystem.EPSG5650,
      x: epsg5650.x,
      y: epsg5650.y
    };

    // SYM_TEXT: Bei Vollprotokoll-Punkten als Nummer, sonst als Bemerkungen
    const symText = props.SYM_TEXT || props.sym_text || '';
    if (istPunktartMitProtokoll(punktart)) {
      // Vollprotokoll: SYM_TEXT → Nummer
      bp.header.nummer = symText || props.Nummer || props.nummer || '';
    } else {
      // Kurzprotokoll/andere: SYM_TEXT → Bemerkungen, Nummer separat
      bp.header.nummer = props.Nummer || props.nummer || '';
      if (symText) {
        bp.header.bemerkungen = symText;
      }
    }

    // HBF übernehmen
    const hbf = props.HBF || props.hbf || '';
    if (hbf && bp.header.bodenform.bf.length > 0) {
      bp.header.bodenform.bf[0].hbf = hbf;
    }

    // Bodenform übernehmen (BF enthält Präfix + FBF + evtl. Suffix + Wasserformen)
    const bf = props.BF || props.bf || '';
    if (bf && bp.header.bodenform.bf.length > 0) {
      const parsed = this.parseFeinbodenform(bf);
      if (parsed.prefix) bp.header.bodenform.prefix = parsed.prefix;
      if (parsed.fbf) bp.header.bodenform.bf[0].fbf = parsed.fbf;
      if (parsed.suffix) bp.header.bodenform.suffix = parsed.suffix;
      if (parsed.stauwasser) bp.header.wasserstufe2 = parsed.stauwasser;
      if (parsed.grundwasser) bp.header.wasserstufe = parsed.grundwasser;
    }

    // HF (Humusform) übernehmen
    const hf = props.HF || props.hf || '';
    if (hf) {
      if (symbol === 7) {
        // Moorpunkt: HF ist Vegetationsformengruppe
        bp.header.vegetationsformengruppe = hf;
      } else {
        bp.header.humusform.aufnahmeFlaeche.neu = hf;
      }
    }

    // Bemerkungen übernehmen (nur wenn nicht bereits durch SYM_TEXT gesetzt)
    const bemerkungenFromProps = props.Bemerkungen || props.bemerkungen || '';
    if (bemerkungenFromProps && !bp.header.bemerkungen) {
      bp.header.bemerkungen = bemerkungenFromProps;
    }

    return bp;
  }

  private validateParsedData(data: AufnahmePunkt, result: ImportValidationResult): void {
    // Koordinaten prüfen
    if (!data.header.koordinaten?.x || !data.header.koordinaten?.y) {
      result.isValid = false;
      result.messages.push({
        severity: 'error',
        field: 'Koordinaten',
        value: '',
        message: 'Koordinaten fehlen oder sind ungültig'
      });
    }

    // Nummer prüfen (Warnung wenn leer)
    if (!data.header.nummer) {
      result.hasWarnings = true;
      result.messages.push({
        severity: 'warning',
        field: 'Nummer',
        value: '',
        message: 'Bohrpunktnummer ist leer'
      });
    }

    // Datum prüfen
    if (!data.header.aufnahme?.datum) {
      result.hasWarnings = true;
      result.messages.push({
        severity: 'warning',
        field: 'Datum',
        value: '',
        message: 'Aufnahmedatum fehlt'
      });
    }

    // Kartierer prüfen
    if (!data.header.aufnahme?.kartierer) {
      result.hasWarnings = true;
      result.messages.push({
        severity: 'warning',
        field: 'Kartierer',
        value: '',
        message: 'Kartierer fehlt'
      });
    }

    // TODO: Weitere Validierungen (Substrate, Horizonte, Vegetation gegen Listen prüfen)
  }

  private createValidationSummary(): void {
    const results: ImportValidationResult[] = [];
    let successCount = 0;
    let warningCount = 0;
    let errorCount = 0;

    for (const file of this.parsedFiles) {
      for (const sheet of file.sheets) {
        results.push(sheet.validation);
        if (!sheet.validation.isValid) {
          errorCount++;
        } else if (sheet.validation.hasWarnings) {
          warningCount++;
        } else {
          successCount++;
        }
      }
    }

    this.validationSummary = {
      totalFiles: results.length,
      successCount,
      warningCount,
      errorCount,
      results
    };
  }

  private async checkDuplicates(): Promise<void> {
    this.duplicates = [];
    this.progressText = 'Prüfe auf Duplikate...';

    // Bestehende Punkte aus DB laden
    try {
      const result = await this.dataService.queryIndex(true);
      if (result?.rows) {
        this.existingPoints = result.rows.map((row: any) => row.doc as AufnahmePunktDocument);
      }
    } catch (error) {
      console.error('Fehler beim Laden bestehender Punkte:', error);
      return;
    }

    // Alle zu importierenden Punkte prüfen
    for (const file of this.parsedFiles) {
      for (const sheet of file.sheets) {
        if (!sheet.data || !sheet.validation.isValid) continue;

        const newPoint = sheet.data;
        const duplicate = this.findDuplicate(newPoint);

        if (duplicate) {
          this.duplicates.push(duplicate);

          // Warnung zur Validierung hinzufügen
          sheet.validation.hasWarnings = true;
          sheet.validation.messages.push({
            severity: 'warning',
            field: 'Duplikat',
            value: duplicate.existingNummer || duplicate.existingId,
            message: this.getDuplicateMessage(duplicate)
          });
        }
      }
    }

    // Summary aktualisieren falls Duplikate gefunden
    if (this.duplicates.length > 0) {
      this.createValidationSummary();
    }
  }

  private findDuplicate(newPoint: AufnahmePunkt): DuplicateInfo | null {
    const COORDINATE_TOLERANCE = 5; // Meter

    for (const existing of this.existingPoints) {
      // 1. Koordinaten-Prüfung (< 5m Entfernung) - ERSTES Kriterium
      if (newPoint.header.koordinaten && existing.header?.koordinaten) {
        const distance = this.calculateDistance(
          newPoint.header.koordinaten,
          existing.header.koordinaten
        );

        if (distance < COORDINATE_TOLERANCE) {
          return {
            newPoint,
            existingId: existing._id,
            existingNummer: existing.header?.nummer,
            matchType: 'koordinaten',
            distance
          };
        }
      }

      // 2. Kombination: Nummer + Datum + Kartierer (alle 3 müssen übereinstimmen)
      if (newPoint.header.nummer && existing.header?.nummer &&
          newPoint.header.aufnahme?.datum && existing.header?.aufnahme?.datum &&
          newPoint.header.aufnahme?.kartierer && existing.header?.aufnahme?.kartierer) {

        // Nummer prüfen (case-insensitive, toString() falls Zahl)
        const nummerMatch = String(newPoint.header.nummer).trim().toLowerCase() ===
                            String(existing.header.nummer).trim().toLowerCase();

        // Datum prüfen (nur Datum, nicht Uhrzeit)
        const newDate = new Date(newPoint.header.aufnahme.datum).toDateString();
        const existingDate = new Date(existing.header.aufnahme.datum).toDateString();
        const datumMatch = newDate === existingDate;

        // Kartierer prüfen (case-insensitive, toString() falls nötig)
        const kartiererMatch = String(newPoint.header.aufnahme.kartierer).trim().toLowerCase() ===
                               String(existing.header.aufnahme.kartierer).trim().toLowerCase();

        // Alle 3 Kriterien erfüllt = Duplikat
        if (nummerMatch && datumMatch && kartiererMatch) {
          return {
            newPoint,
            existingId: existing._id,
            existingNummer: existing.header.nummer,
            matchType: 'datum_kartierer'
          };
        }
      }
    }

    return null;
  }

  private calculateDistance(coord1: { x: number; y: number }, coord2: { x: number; y: number }): number {
    // Euklidische Distanz für projizierte Koordinaten (EPSG:5650/25833)
    const dx = coord1.x - coord2.x;
    const dy = coord1.y - coord2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private getDuplicateMessage(duplicate: DuplicateInfo): string {
    switch (duplicate.matchType) {
      case 'koordinaten':
        return `Punkt mit gleichen Koordinaten existiert bereits (${duplicate.distance?.toFixed(1)}m entfernt)`;
      case 'datum_kartierer':
        return `Punkt mit gleicher Nummer, Datum und Kartierer existiert bereits`;
      default:
        return `Duplikat gefunden`;
    }
  }

  // === Vorschau ===

  get validPoints(): AufnahmePunkt[] {
    const points: AufnahmePunkt[] = [];
    for (const file of this.parsedFiles) {
      for (const sheet of file.sheets) {
        if (sheet.data && sheet.validation.isValid) {
          points.push(sheet.data);
        }
      }
    }
    return points;
  }

  get warningPoints(): AufnahmePunkt[] {
    const points: AufnahmePunkt[] = [];
    for (const file of this.parsedFiles) {
      for (const sheet of file.sheets) {
        if (sheet.data && sheet.validation.isValid && sheet.validation.hasWarnings) {
          points.push(sheet.data);
        }
      }
    }
    return points;
  }

  get pointsToImport(): number {
    let count = 0;
    if (this.importOptions.importValid) {
      count += this.validationSummary?.successCount || 0;
    }
    if (this.importOptions.importWithWarnings) {
      count += this.validationSummary?.warningCount || 0;
    }
    return count;
  }

  async copyValidationToClipboard(): Promise<void> {
    if (!this.validationSummary) return;

    const text = formatValidationForClipboard(this.validationSummary);
    const success = await copyToClipboard(text);

    if (success) {
      this.toastr.success('Validierungsbericht in Zwischenablage kopiert');
    } else {
      this.toastr.error('Kopieren fehlgeschlagen');
    }
  }

  // === Import ===

  async startImport(): Promise<void> {
    this.step = 'importing';
    this.progress = 0;
    this.importedCount = 0;
    this.skippedCount = 0;

    const pointsToImport: AufnahmePunkt[] = [];
    const pointsToUpdate: { existing: AufnahmePunktDocument; newData: AufnahmePunkt }[] = [];

    // Duplikat-IDs für schnellen Lookup
    const duplicatePointMap = new Map<AufnahmePunkt, DuplicateInfo>();
    for (const dup of this.duplicates) {
      duplicatePointMap.set(dup.newPoint, dup);
    }

    for (const file of this.parsedFiles) {
      for (const sheet of file.sheets) {
        if (!sheet.data) continue;

        // Import-Info zum Punkt hinzufügen
        if (sheet.importInfo) {
          sheet.data.importInfo = sheet.importInfo;
        }

        // Prüfen ob dieser Punkt ein Duplikat ist
        const duplicate = duplicatePointMap.get(sheet.data);
        const hasDuplicate = !!duplicate;

        // Prüfen ob Punkt aufgrund Validierung importiert werden soll
        const passesValidation =
          (sheet.validation.isValid && !sheet.validation.hasWarnings && this.importOptions.importValid) ||
          (sheet.validation.isValid && sheet.validation.hasWarnings && this.importOptions.importWithWarnings);

        if (!passesValidation) {
          this.skippedCount++;
          continue;
        }

        // Duplikat-Behandlung
        if (hasDuplicate) {
          switch (this.importOptions.duplicateAction) {
            case 'skip':
              this.skippedCount++;
              continue;

            case 'update':
              // Bestehenden Punkt zum Update vormerken
              const existing = this.existingPoints.find(p => p._id === duplicate.existingId);
              if (existing) {
                pointsToUpdate.push({ existing, newData: sheet.data });
              }
              continue;

            case 'keep_both':
              // Beide behalten - neuen Punkt importieren
              pointsToImport.push(sheet.data);
              continue;
          }
        } else {
          // Kein Duplikat - normal importieren
          pointsToImport.push(sheet.data);
        }
      }
    }

    const totalOperations = pointsToImport.length + pointsToUpdate.length;
    if (totalOperations === 0) {
      this.step = 'complete';
      return;
    }

    // Neue Punkte importieren
    if (pointsToImport.length > 0) {
      this.progressText = `Importiere ${pointsToImport.length} neue Punkte...`;

      try {
        await this.dataService.addBohrpunkte(pointsToImport);
        this.importedCount += pointsToImport.length;
      } catch (error: any) {
        this.toastr.error(`Import fehlgeschlagen: ${error.message}`);
      }
    }

    // Bestehende Punkte aktualisieren
    if (pointsToUpdate.length > 0) {
      this.progressText = `Aktualisiere ${pointsToUpdate.length} bestehende Punkte...`;
      for (const { existing, newData } of pointsToUpdate) {
        try {
          // Merge: Bestehende Dokument-Metadaten beibehalten, Daten aktualisieren
          // _rev wird von PouchDB hinzugefügt, ist aber nicht im TypeScript-Interface
          const existingWithRev = existing as any;
          const updatedDoc = {
            ...existing,
            ...newData,
            _id: existing._id,
            _rev: existingWithRev._rev
          };
          await this.dataService.saveDocument(updatedDoc);
          this.importedCount++;
        } catch (error: any) {
          console.error(`Fehler beim Aktualisieren von ${existing._id}:`, error);
          this.skippedCount++;
        }
      }
    }

    if (this.importedCount > 0) {
      this.toastr.success(`${this.importedCount} Punkte importiert/aktualisiert`);
    }

    this.step = 'complete';
  }

  // === Navigation ===

  close(): void {
    this.activeModal.close({
      imported: this.importedCount,
      skipped: this.skippedCount
    });
  }

  cancel(): void {
    this.activeModal.dismiss('cancel');
  }

  /**
   * Erstellt und speichert eine Import-Logdatei.
   */
  saveImportLog(): void {
    const lines: string[] = [];
    const now = new Date();

    // Header
    lines.push('='.repeat(60));
    lines.push('IMPORT-PROTOKOLL');
    lines.push('='.repeat(60));
    lines.push(`Datum: ${now.toLocaleDateString('de-DE')} ${now.toLocaleTimeString('de-DE')}`);
    lines.push('');

    // Zusammenfassung
    lines.push('-'.repeat(60));
    lines.push('ZUSAMMENFASSUNG');
    lines.push('-'.repeat(60));
    lines.push(`Dateien analysiert: ${this.parsedFiles.length}`);
    if (this.validationSummary) {
      lines.push(`Protokolle gesamt: ${this.validationSummary.totalFiles}`);
      lines.push(`  - Fehlerfrei: ${this.validationSummary.successCount}`);
      lines.push(`  - Mit Warnungen: ${this.validationSummary.warningCount}`);
      lines.push(`  - Mit Fehlern: ${this.validationSummary.errorCount}`);
    }
    lines.push(`Duplikate gefunden: ${this.duplicates.length}`);
    lines.push('');
    lines.push(`Importiert: ${this.importedCount}`);
    lines.push(`Übersprungen: ${this.skippedCount}`);
    lines.push('');

    // Detaillierte Ergebnisse pro Datei
    lines.push('-'.repeat(60));
    lines.push('DETAILS PRO DATEI');
    lines.push('-'.repeat(60));

    for (const file of this.parsedFiles) {
      lines.push('');
      lines.push(`Datei: ${file.filename}`);
      lines.push(`  Sheets: ${file.sheets.length}`);

      for (const sheet of file.sheets) {
        const status = !sheet.validation.isValid ? '[FEHLER]' :
                       sheet.validation.hasWarnings ? '[WARNUNG]' : '[OK]';
        lines.push(`  ${status} ${sheet.name} (${sheet.format})`);

        // Nummer und Koordinaten wenn vorhanden
        if (sheet.data) {
          const nummer = sheet.data.header?.nummer || '(keine Nummer)';
          const koord = sheet.data.header?.koordinaten;
          if (koord) {
            lines.push(`         Nummer: ${nummer}`);
            lines.push(`         Koordinaten: ${koord.x}, ${koord.y}`);
          }
        }

        // Meldungen
        for (const msg of sheet.validation.messages) {
          const prefix = msg.severity === 'error' ? '    [!]' :
                        msg.severity === 'warning' ? '    [?]' : '    [i]';
          lines.push(`${prefix} ${msg.field}: ${msg.message}`);
          if (msg.value) {
            lines.push(`        Wert: "${msg.value}"`);
          }
        }
      }
    }

    // Duplikate
    if (this.duplicates.length > 0) {
      lines.push('');
      lines.push('-'.repeat(60));
      lines.push('DUPLIKATE');
      lines.push('-'.repeat(60));
      for (const dup of this.duplicates) {
        const nummer = dup.newPoint.header?.nummer || '(keine Nummer)';
        lines.push(`  ${nummer} -> existiert bereits als ${dup.existingNummer || dup.existingId}`);
        lines.push(`    Grund: ${dup.matchType === 'koordinaten' ? `Koordinaten (${dup.distance?.toFixed(1)}m)` : 'Nummer/Datum/Kartierer'}`);
      }
    }

    lines.push('');
    lines.push('='.repeat(60));
    lines.push('ENDE DES PROTOKOLLS');
    lines.push('='.repeat(60));

    // Datei erstellen und herunterladen
    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-log_${now.toISOString().slice(0, 10)}_${now.toISOString().slice(11, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // === Feinbodenform Parser ===

  /**
   * Parst eine kombinierte Feinbodenform-Zeichenkette in Präfix, FBF, Suffix und Wasserformen.
   *
   * Wasserform-Erkennung basiert auf dem Kürzel in der FBF:
   *   - Stauwasser: L, U, T, dL, tL, dT, tT, K, dK, tK
   *   - Grundwasser: S, F, G, R, bS und sonstiges (Default)
   *   - Format Zahl/Zahl → immer Stau/Grund
   *   - Substratfeuchte nach "-" am Ende wird ignoriert
   */
  private parseFeinbodenform(input: string): { prefix: string; fbf: string; suffix: string; stauwasser: string; grundwasser: string } {
    if (!input) return { prefix: '', fbf: '', suffix: '', stauwasser: '', grundwasser: '' };

    let remaining = input.trim();
    let suffix = '';
    let stauwasser = '';
    let grundwasser = '';

    // 0. Substratfeuchte am Ende entfernen (nach "-" am Ende)
    const substratFeuchteMatch = remaining.match(/-[a-z]{1,2}$/);
    if (substratFeuchteMatch) {
      remaining = remaining.substring(0, remaining.length - substratFeuchteMatch[0].length);
    }

    // 1. Wasserformen und Suffix extrahieren
    const waterMatch = remaining.match(/(\d+)(?:\/(\d+))?([a-z]*)$/);
    if (waterMatch) {
      const num1 = waterMatch[1];
      const num2 = waterMatch[2] || '';
      const trailingSuffix = waterMatch[3] || '';

      if (trailingSuffix) {
        suffix = trailingSuffix;
      }

      remaining = remaining.substring(0, remaining.length - waterMatch[0].length);

      // Bestimmen ob Stau- oder Grundwasser anhand des FBF-Kürzels
      const isStau = this.isStauwasserSubstrat(remaining);

      if (num2) {
        // Format Zahl/Zahl → Stau/Grund
        stauwasser = num1;
        grundwasser = num2;
      } else if (isStau) {
        stauwasser = num1;
      } else {
        grundwasser = num1;
      }
    } else {
      // Kein Wasserformen-Match, prüfen auf normalen Suffix nach "/"
      const slashIndex = remaining.indexOf('/');
      if (slashIndex !== -1) {
        const afterSlash = remaining.substring(slashIndex + 1).trim();
        if (!/^\d/.test(afterSlash)) {
          suffix = afterSlash;
          remaining = remaining.substring(0, slashIndex);
        }
      }
    }

    // 2. Präfix extrahieren
    const prefixes = ['Ap', 'po', 'rb', 'tp', 'b', 'g', 'h', 'o', 'p', 'q', 's', 'w', 'a', 'x'];
    let prefix = '';

    let foundPrefix = true;
    while (foundPrefix && remaining.length > 0) {
      foundPrefix = false;
      for (const p of prefixes) {
        if (remaining.startsWith(p)) {
          const afterPrefix = remaining.substring(p.length);
          if (afterPrefix.length === 0 || /^[A-ZÄÖÜ]/.test(afterPrefix) || prefixes.some(px => afterPrefix.startsWith(px))) {
            prefix += p;
            remaining = afterPrefix;
            foundPrefix = true;
            break;
          }
        }
      }
    }

    // 3. FBF: Rest
    const fbf = remaining;

    return { prefix, fbf, suffix, stauwasser, grundwasser };
  }

  /**
   * Prüft ob das Substrat zu Stauwasser gehört (sonst Grundwasser).
   *
   * FBF = Individualteil (Groß+Kleinbuchstabe) + Kürzel
   * Das Kürzel beginnt mit dem Substrat-Präfix.
   *
   * Stauwasser: L, U, T, dL, tL, dT, tT, K, dK, tK
   * Grundwasser: S, F, G, R, bS und alles sonstige (Default)
   */
  private isStauwasserSubstrat(fbf: string): boolean {
    if (!fbf || fbf.length <= 2) return false;

    // Individualteil überspringen (Großbuchstabe + Kleinbuchstabe)
    const match = fbf.match(/^[A-ZÄÖÜ][a-zäöü]/);
    if (!match) return false;

    const kuerzel = fbf.substring(match[0].length);
    if (!kuerzel) return false;

    // Stauwasser-Präfixe (längere zuerst)
    const stauPraefixe = ['dL', 'tL', 'dT', 'tT', 'dK', 'tK', 'L', 'U', 'T', 'K'];
    for (const p of stauPraefixe) {
      if (kuerzel.startsWith(p)) return true;
    }

    return false;
  }
}
